// Admin panel — user & license management + support tickets
import { api } from './api.js';
import { initAdminTickets } from './admin-tickets.js';
import { initAdminTestimonials } from './admin-testimonials.js';
import { initAdminActivity } from './admin-activity.js';
import { initAdminEmailLog } from './admin-email-log.js';
import { initAdminDevLog } from './admin-devlog.js';
import { initEmailStudio } from './email/studio.js';
import { initAdminSubscribers } from './admin-subscribers.js';
import { initAdminBilling } from './admin-billing.js';
import { initAdminShares } from './admin-shares.js';
import { initAdminAdverts } from './admin-adverts.js';
import { initAdminTerms } from './admin-terms.js';

// Two-group mod toggle: physics MODULES (paid subscriptions) and access ROLES
// (admin grants for protected docs / theory). Same underlying module list, but
// rendered in two visually distinct rows so trust-grants don't get confused
// with paid subscriptions in the admin panel.
const PHYSICS_MODULES = ['plasma', 'medical', 'cosmic', 'materials', 'geophysics', 'fluids', 'quantum', 'hpclab', 'theory'];
const ROLE_MODULES    = ['app_guide', 'app_guide_nda', 'dso', 'invest'];
// PRODUCT add-on modules: PAID per-seat products (not free trust-grants), so
// they get their own mod-group row distinct from Roles. 'worklog' = the
// GDBSPro+Worklog seat. Granted by Stripe self-purchase OR an admin mod-pill
// here; revoking removes the seat. NEVER add a product module to ROLE_MODULES.
// 'gate' = the Gate client-portal add-on ($9.99/user/mo on every tier; unlike
// worklog it is NOT auto-included at Pro/HPC - paid on every tier).
const PRODUCT_MODULES = ['worklog', 'gate'];
const ALL_MODULES = [...PHYSICS_MODULES, ...ROLE_MODULES, ...PRODUCT_MODULES];
const MODULE_LABELS = {
  plasma: 'Plasma', medical: 'Medical', cosmic: 'Cosmos',
  materials: 'Materials', geophysics: 'Geophysics', fluids: 'Fluids', quantum: 'Quantum', hpclab: 'HPC Lab', theory: 'Theory',
  // App Guide RBAC roles (admin grants per user). app_guide = Tier 1 (redacted);
  // app_guide_nda = Tier 2 (full, NDA). NDA implies App Guide access.
  app_guide: 'App Guide', app_guide_nda: 'App Guide (NDA)',
  // DSO is a novel-theory role: admin-only by default, granted on user request
  // or by explicit owner assignment. No document is gated to "dso" by default;
  // the role is provisioned so future DSO content can be opted into.
  dso: 'DSO (gated)',
  // Investor access: per-user grant for the gated Investor Dashboard (served
  // via /api/docs, never public). Distinct from app_guide_nda so an investor
  // does NOT also get gLattice/tech NDA docs (least privilege).
  invest: 'Investor (NDA)',
  // Worklog seat add-on (paid product, $15/seat/mo). Entitlement only; in-app
  // workflow roles (manager/legal/intake/...) are separate data, never here.
  worklog: 'Worklog',
  // Gate client-portal add-on (paid product, $9.99/user/mo on every tier).
  // Entitlement only; per-portal capability roles (owner/editor/viewer) and
  // external client identities are Gate data resolved server-side, never here.
  gate: 'Gate'
};

let _users = [];

export function init() {
  // NOTE: this runs at app boot, BEFORE login completes, so getUser() is null
  // here for everyone - do NOT gate the wiring on role or the tab-switching never
  // attaches (and no admin tab works). Authorization is enforced where it belongs:
  // each panel's refresh()/load self-guards on role before it calls an endpoint,
  // by which point getUser() is populated. Non-admins never see this view anyway.
  document.getElementById('btn-admin-refresh')?.addEventListener('click', loadUsers);
  initBetaCapacity();
  initAdminTickets();
  initAdminTestimonials();
  initAdminActivity();
  initAdminEmailLog();
  initAdminDevLog();
  initEmailStudio();
  initAdminSubscribers();
  initAdminBilling();
  initAdminShares();
  initAdminAdverts();
  initAdminTerms(); // wires the Search Terms tab loader + Refresh / Re-ingest buttons (was imported but never called)
}

// Founding-beta capacity controls: show usage, open +10 slots, or set an
// absolute cap. The count is the server's source of truth (beta-status).
function initBetaCapacity() {
  document.getElementById('btn-beta-add10')?.addEventListener('click', async () => {
    const res = await api.adminSetBetaCapacity({ add: 10 });
    if (res.success) renderBetaCap(res.data);
    else alert(res.error || 'Failed to add slots');
  });
  document.getElementById('btn-beta-setcap')?.addEventListener('click', async () => {
    const v = parseInt(document.getElementById('beta-cap-input')?.value, 10);
    if (isNaN(v) || v < 0) { alert('Enter a non-negative capacity.'); return; }
    const res = await api.adminSetBetaCapacity({ capacity: v });
    if (res.success) renderBetaCap(res.data);
    else alert(res.error || 'Failed to set capacity');
  });
}

function renderBetaCap(d) {
  if (!d) return;
  const readout = document.getElementById('beta-cap-readout');
  const input = document.getElementById('beta-cap-input');
  if (readout) readout.textContent = `${d.used} used / ${d.capacity} cap - ${d.remaining} open` + (d.open ? '' : ' (full)');
  if (input && document.activeElement !== input) input.value = d.capacity;
}

async function loadBetaCap() {
  try {
    const res = await api.adminBetaCapacity();
    if (res.success) renderBetaCap(res.data);
  } catch (_) { /* endpoint may not be deployed yet */ }
}

// Admin-gated nav-items split in two:
//   - alwaysAdmin: revealed automatically when user.role === 'admin'
//       (Admin panel — the user expects this to be there)
//   - toggleAdmin: hidden by default even for admins; only the admin can
//       reveal them via the "Show admin views" toggle in the user dropdown.
//       (Query, Browser, Import — raw database surfaces that clutter the
//       sidebar for daily research work)
// btn-tech-guide visibility is handled by app-guide.js (admin OR NDA), since the
// Technical Guide is now served through the role-gated /api/docs endpoint.
const ALWAYS_ADMIN = ['nav-admin'];
const TOGGLE_ADMIN = ['nav-query', 'nav-browser', 'nav-import'];
const TOGGLE_KEY   = 'gdbs_admin_views_shown_v1';

export function showAdminIfAllowed(user) {
  const isAdmin = user?.role === 'admin';

  for (const id of ALWAYS_ADMIN) {
    const el = document.getElementById(id);
    if (!el) continue;
    if (isAdmin) el.classList.remove('hidden');
    else         el.classList.add('hidden');
  }

  // The toggle entry in the user dropdown is admin-only.
  const toggleBtn = document.getElementById('btn-admin-views');
  if (toggleBtn) {
    if (isAdmin) toggleBtn.classList.remove('hidden');
    else         toggleBtn.classList.add('hidden');
  }

  // Apply persisted toggle state for admins. Non-admins always have the
  // toggle-gated views hidden, regardless of the localStorage flag.
  const shown = isAdmin && localStorage.getItem(TOGGLE_KEY) === '1';
  applyAdminViewsVisibility(shown);

  // If a now-hidden view is currently active, route to home.
  const activeView = document.querySelector('.view.active');
  const activeId = activeView?.id || '';
  const blockedForUser = !isAdmin && ['view-admin', 'view-query', 'view-browser', 'view-import'].includes(activeId);
  const blockedForAdmin = isAdmin && !shown && ['view-query', 'view-browser', 'view-import'].includes(activeId);
  if (blockedForUser || blockedForAdmin) navigateHome();
}

// Reveal/hide the toggle-gated sidebar items and reflect state in the menu icon.
function applyAdminViewsVisibility(shown) {
  for (const id of TOGGLE_ADMIN) {
    const el = document.getElementById(id);
    if (!el) continue;
    el.classList.toggle('hidden', !shown);
  }
  const icon = document.querySelector('#btn-admin-views .user-admin-icon');
  const label = document.querySelector('#btn-admin-views .user-admin-label');
  if (icon) {
    icon.classList.toggle('fa-check-square', shown);
    icon.classList.toggle('fa-square', !shown);
  }
  if (label) label.textContent = shown ? 'Hide admin views' : 'Show admin views';
}

// Public toggle hooked up from the user dropdown.
export function toggleAdminViews() {
  const current = localStorage.getItem(TOGGLE_KEY) === '1';
  const next = !current;
  if (next) localStorage.setItem(TOGGLE_KEY, '1');
  else      localStorage.removeItem(TOGGLE_KEY);
  applyAdminViewsVisibility(next);
}
// Bridge for the inline onclick in the dropdown item.
if (typeof window !== 'undefined') {
  window.gdbsToggleAdminViews = toggleAdminViews;
}

/// Navigate to the first non-hidden nav-item (the user's "home" landing).
/// Used by the GDBSPro brand-click and after gating updates.
export function navigateHome() {
  // Prefer the dedicated Home dashboard (default landing), regardless of its position in the nav; fall
  // back to the first visible module only if the Home item is absent.
  const home = document.querySelector('.sidebar-nav .nav-item[data-view="home"]:not(.hidden)');
  if (home) { home.click(); return; }
  const firstVisible = document.querySelector('.sidebar-nav .nav-item:not(.hidden)');
  if (firstVisible) firstVisible.click();
}

export async function loadUsers() {
  const tbody = document.getElementById('admin-users-body');
  if (!tbody) return;
  tbody.innerHTML = '<tr><td colspan="7" style="text-align:center;color:var(--text-dim)">Loading...</td></tr>';

  const res = await api.adminUsers();
  if (!res.success) {
    tbody.innerHTML = `<tr><td colspan="7" class="admin-empty">${res.error || 'Failed to load users'}</td></tr>`;
    return;
  }

  _users = res.data || [];
  renderTable();
  loadBetaCap();
}

function renderTable() {
  const tbody = document.getElementById('admin-users-body');
  if (!_users.length) {
    tbody.innerHTML = '<tr><td colspan="7" class="admin-empty">No users found</td></tr>';
    return;
  }

  tbody.innerHTML = _users.map(u => {
    const mods = u.license?.modules || [];
    const tier = u.license?.tier || 'none';
    const disabled = u.status === 'disabled';
    const pill = (m, isRole) => {
      const active = mods.includes(m);
      const cls = `mod-pill${isRole ? ' mod-role' : ''}${active ? ' mod-active' : ''}`;
      return `<span class="${cls}" data-uid="${u.id}" data-mod="${m}" title="${active ? 'Click to revoke' : 'Click to grant'} - ${MODULE_LABELS[m]}">${MODULE_LABELS[m]}</span>`;
    };
    const modPills =
      `<div class="mod-group"><span class="mod-group-label">Modules</span>${PHYSICS_MODULES.map(m => pill(m, false)).join('')}</div>` +
      `<div class="mod-group"><span class="mod-group-label">Roles</span>${ROLE_MODULES.map(m => pill(m, true)).join('')}</div>` +
      `<div class="mod-group"><span class="mod-group-label">Products</span>${PRODUCT_MODULES.map(m => pill(m, false)).join('')}</div>`;

    return `<tr${disabled ? ' style="opacity:0.6;"' : ''}>
      <td class="admin-cell-email">${esc(u.email)}${disabled ? ' <span style="color:#f87171;font-size:10px;font-weight:700;">DISABLED</span>' : ''}</td>
      <td>${esc(u.displayName)}</td>
      <td>
        <select class="admin-select" data-uid="${u.id}" data-field="role">
          <option value="user" ${u.role === 'user' ? 'selected' : ''}>user</option>
          <option value="admin" ${u.role === 'admin' ? 'selected' : ''}>admin</option>
        </select>
      </td>
      <td>
        <select class="admin-select" data-uid="${u.id}" data-field="tier">
          <option value="trial" ${tier === 'trial' ? 'selected' : ''}>trial</option>
          <option value="standard" ${tier === 'standard' ? 'selected' : ''}>standard</option>
          <option value="pro" ${tier === 'pro' ? 'selected' : ''}>pro</option>
          <option value="enterprise" ${tier === 'enterprise' ? 'selected' : ''}>HPC</option>
          <option value="research" ${tier === 'research' ? 'selected' : ''}>research</option>
          <option value="beta" ${tier === 'beta' ? 'selected' : ''}>beta</option>
        </select>
        ${u.license?.betaIntent ? `<div style="margin-top:4px;font-size:10px;white-space:nowrap;">
          <span title="Declared purchase intent if GDBS bridges to HPC" style="color:#6366f1;font-weight:600;">intent: ${esc(u.license.betaIntent)}</span>
          ${tier === 'beta' ? `<button class="btn btn-sm admin-beta-extend" data-uid="${u.id}" title="Extend founding-beta to 30 days" style="color:#6366f1;border-color:#6366f1;padding:1px 5px;margin-left:4px;"><i class="fas fa-clock"></i> 30d</button>` : ''}
        </div>` : ''}
      </td>
      <td class="admin-cell-modules">${modPills}</td>
      <td>${u.createdAt ? new Date(u.createdAt).toLocaleDateString() : ''}</td>
      <td class="admin-cell-actions">
        <button class="btn btn-sm admin-email" data-uid="${u.id}" title="Email license"><i class="fas fa-envelope"></i></button>
        <button class="btn btn-sm admin-bssn-boost" data-uid="${u.id}" title="Grant +200K BSSN compute hours" style="color:#fbbf24;border-color:#fbbf24;"><i class="fas fa-bolt"></i></button>
        ${disabled
          ? `<button class="btn btn-sm admin-enable" data-uid="${u.id}" title="Re-enable account" style="color:#4ade80;border-color:#4ade80;"><i class="fas fa-unlock"></i></button>`
          : `<button class="btn btn-sm admin-disable" data-uid="${u.id}" title="Disable account" style="color:#fbbf24;border-color:#fbbf24;"><i class="fas fa-ban"></i></button>`
        }
        <button class="btn btn-sm btn-danger admin-del" data-uid="${u.id}" title="Delete user"><i class="fas fa-trash"></i></button>
      </td>
    </tr>`;
  }).join('');

  tbody.querySelectorAll('.admin-select[data-field="role"]').forEach(sel => {
    sel.addEventListener('change', async e => {
      const uid = e.target.dataset.uid;
      await api.adminSetRole(uid, e.target.value);
      loadUsers();
    });
  });

  tbody.querySelectorAll('.admin-select[data-field="tier"]').forEach(sel => {
    sel.addEventListener('change', async e => {
      const uid = e.target.dataset.uid;
      await api.adminSetTier(uid, e.target.value);
      loadUsers();
    });
  });

  tbody.querySelectorAll('.mod-pill').forEach(pill => {
    pill.addEventListener('click', async e => {
      const uid = e.target.dataset.uid;
      const mod = e.target.dataset.mod;
      const active = e.target.classList.contains('mod-active');
      if (active) {
        await api.adminRevokeModule(uid, mod);
      } else {
        await api.adminGrantModule(uid, mod);
      }
      loadUsers();
    });
  });

  tbody.querySelectorAll('.admin-email').forEach(btn => {
    btn.addEventListener('click', async e => {
      const uid = e.currentTarget.dataset.uid;
      const user = _users.find(u => u.id === uid);
      btn.disabled = true;
      btn.innerHTML = '<i class="fas fa-spinner fa-spin"></i>';
      const res = await api.adminEmailLicense(uid);
      if (res.success) {
        btn.innerHTML = '<i class="fas fa-check"></i>';
        setTimeout(() => { btn.innerHTML = '<i class="fas fa-envelope"></i>'; btn.disabled = false; }, 2000);
      } else {
        alert(res.error || 'Failed to send email');
        btn.innerHTML = '<i class="fas fa-envelope"></i>';
        btn.disabled = false;
      }
    });
  });

  tbody.querySelectorAll('.admin-bssn-boost').forEach(btn => {
    btn.addEventListener('click', async e => {
      const uid = e.currentTarget.dataset.uid;
      const user = _users.find(u => u.id === uid);
      // Default action: grant +200K CHE (one full base bundle).
      // Hold Shift while clicking to set a 2× permanent multiplier instead.
      const isMultiplier = e.shiftKey;
      const reason = prompt(
        isMultiplier
          ? `Apply 2× CHE multiplier (permanent) for ${user?.email || uid}?\nReason (logged):`
          : `Grant +200,000 BSSN CHE to ${user?.email || uid}?\nReason (logged):`,
        ''
      );
      if (reason === null) return;
      btn.disabled = true;
      btn.innerHTML = '<i class="fas fa-spinner fa-spin"></i>';
      const payload = isMultiplier ? { multiplier: 2, reason } : { extra_che: 200000, reason };
      const res = await api.adminBoostBssnBudget(uid, payload);
      if (res.success) {
        btn.innerHTML = '<i class="fas fa-check"></i>';
        setTimeout(() => { btn.innerHTML = '<i class="fas fa-bolt"></i>'; btn.disabled = false; }, 1500);
        loadUsers();
      } else {
        alert(res.error || 'Failed to apply BSSN boost — backend endpoint may not be wired yet.');
        btn.innerHTML = '<i class="fas fa-bolt"></i>';
        btn.disabled = false;
      }
    });
  });

  tbody.querySelectorAll('.admin-beta-extend').forEach(btn => {
    btn.addEventListener('click', async e => {
      const uid = e.currentTarget.dataset.uid;
      const user = _users.find(u => u.id === uid);
      if (!confirm(`Extend the founding-beta window to 30 days for ${user?.email || uid}?`)) return;
      const res = await api.adminExtendBeta(uid);
      if (res.success) loadUsers();
      else alert(res.error || 'Failed to extend beta');
    });
  });

  tbody.querySelectorAll('.admin-disable').forEach(btn => {
    btn.addEventListener('click', async e => {
      const uid = e.currentTarget.dataset.uid;
      const user = _users.find(u => u.id === uid);
      const reason = prompt(`Disable reason for ${user?.email || uid} (shown to user):`, 'Your account has been disabled by an administrator.');
      if (reason === null) return;
      await api.adminDisableUser(uid, reason);
      loadUsers();
    });
  });

  tbody.querySelectorAll('.admin-enable').forEach(btn => {
    btn.addEventListener('click', async e => {
      const uid = e.currentTarget.dataset.uid;
      const user = _users.find(u => u.id === uid);
      if (!confirm(`Re-enable account for ${user?.email || uid}?`)) return;
      await api.adminEnableUser(uid);
      loadUsers();
    });
  });

  tbody.querySelectorAll('.admin-del').forEach(btn => {
    btn.addEventListener('click', async e => {
      const uid = e.currentTarget.dataset.uid;
      const user = _users.find(u => u.id === uid);
      if (!confirm(`Delete user ${user?.email || uid}?`)) return;
      await api.adminDeleteUser(uid);
      loadUsers();
    });
  });
}

function esc(s) {
  const d = document.createElement('div');
  d.textContent = s || '';
  return d.innerHTML;
}
